import React, { useContext } from "react";
import { Checkbox } from "@panda0/panda0-ui";
import { Eye, MessageSquareText, Star } from "lucide-react";

import TagBadge from "@/ui/links/tag-badge";
import { getRankEmoji } from "./get-rank-emoji";
import { CandidateListContext, InsightsDisplayContext } from "./types";

// Candidate Row Component (used in compact view)
const CandidateRow = ({ candidate, openCandidateModal }) => {
  const { selectedCandidates, setSelectedCandidates } =
    useContext(CandidateListContext);
  const { displayProperties } = useContext(InsightsDisplayContext);
  const { shortlistedCandidates } = useContext(CandidateListContext);

  const handleSelectCandidate = () => {
    const newSelectedCandidates = new Set(selectedCandidates);
    if (newSelectedCandidates.has(candidate.id)) {
      newSelectedCandidates.delete(candidate.id);
    } else {
      newSelectedCandidates.add(candidate.id);
    }
    setSelectedCandidates(newSelectedCandidates);
  };

  return (
    <tr className="border-b border-neutral-200 hover:bg-neutral-50">
      <td className="p-3">
        <Checkbox
          checked={selectedCandidates.has(candidate.id)}
          onCheckedChange={handleSelectCandidate}
          aria-label={`Select candidate ${candidate.id}`}
        />
        <span className="ml-1">{getRankEmoji(candidate.rank)}</span>
      </td>

      {displayProperties.includes("rank") && (
        <td className="p-3">
          <div>
            {(candidate.shortlisted ||
              //@ts-ignore
              shortlistedCandidates?.includes(candidate.id)) && (
              <div>
                <p className="flex h-[0px] w-[80px] items-center justify-center rounded-[10px] bg-[#C4FACA] dark:text-black text-center text-xs font-medium ml-0 mb-1 p-3">
                  Shortlisted <span>🏅</span>
                </p>
              </div>
            )}
            <div className="flex items-center">
              <span className="text-black">{candidate.rank}</span>
            </div>
          </div>
        </td>
      )}

      {displayProperties.includes("jobFit") && (
        <td className="p-3 w-[115px]">
          <div className="flex items-center gap-2">
            <svg className="w-[22%]" viewBox="0 0 36 36">
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#E6E6E6"
                strokeWidth="4"
              />
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#15a349"
                strokeWidth="4"
                strokeDasharray={`${parseInt(candidate.jobFit)}, 100`}
              />
            </svg>
            <p className="text-green-600">{`${candidate.jobFit}%`}</p>
          </div>
        </td>
      )}

      {displayProperties.includes("name") && (
        <td className="p-3">
          <div className="flex items-center gap-2">
            <span className="text-black">{candidate.name}</span>
          </div>
        </td>
      )}
      {displayProperties.includes("score") && (
        <td className="p-3 text-center">
          <TagBadge
            name={candidate.score}
            color={
              candidate.score >= 90
                ? "green"
                : candidate.score >= 70
                  ? "yellow"
                  : "red"
            }
            className={`animate-fade-in text-center w-[80px] ${candidate.score >= 90 ? "bg-green-100 border border-green-300 text-green-600" : candidate.score >= 70 ? "bg-yellow-100 border border-yellow-300 text-yellow-600" : "bg-red-100 border border-red-300 text-red-600"}`}
          />
        </td>
      )}

      {displayProperties.includes("source") && (
        <td className="p-3">
          <span className="text-black">{candidate.source}</span>
        </td>
      )}

      {displayProperties.includes("date") && (
        <td className="p-3">
          <span className="text-black">{candidate.date}</span>
        </td>
      )}

      {displayProperties.includes("ratings") && (
        <td className="p-3">
          <div className="flex items-center">
            <Star className="h-4 w-4 text-yellow-400 mr-1" />
            <span>{candidate.ratings}</span>
          </div>
        </td>
      )}

      {displayProperties.includes("actions") && (
        <td className="p-3">
          <div className="flex gap-2 items-center w-14 h-10">
            <div className="relative">
              <MessageSquareText className="h-5 w-5 text-black cursor-pointer hover:text-black" />
            </div>
            <Eye
              className="h-5 w-5 text-[#000000] cursor-pointer hover:text-[#000000]"
              onClick={() => openCandidateModal(candidate.id)}
            />
          </div>
        </td>
      )}
    </tr>
  );
};

export default React.memo(CandidateRow);
